import { CashFlowSection } from "@common/helpers/domains/accounting/cashFlowClassification";
import {
    CashFlowStatementResponse,
    ReportPeriodWithLabel,
    ReportWithNestedGroupsRow,
    ReportWithNestedGroupsRowGroup,
} from "@common/types/reports/types";
import { CashFlowStatementViewRow } from "./cashFlowStatementViewTypes";

// ---------------------------------------------------------------------------
// Section labels
// ---------------------------------------------------------------------------

const SECTION_LABELS: Record<CashFlowSection, string> = {
    [CashFlowSection.Operating]: "Operating activities",
    [CashFlowSection.Investing]: "Investing activities",
    [CashFlowSection.Financing]: "Financing activities",
};

const SECTION_TOTAL_LABELS: Record<CashFlowSection, string> = {
    [CashFlowSection.Operating]: "Net cash from operating activities",
    [CashFlowSection.Investing]: "Net cash from investing activities",
    [CashFlowSection.Financing]: "Net cash from financing activities",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getNestedAccountCodes(row: ReportWithNestedGroupsRow): string[] {
    if ("rows" in row) {
        return row.rows.flatMap(getNestedAccountCodes);
    }
    return [row.key];
}

let rowIdCounter = 0;
function nextId(prefix: string): string {
    return `${prefix}:${rowIdCounter++}`;
}

// ---------------------------------------------------------------------------
// Core recursive builders
// ---------------------------------------------------------------------------

function buildGroupChildren(
    group: ReportWithNestedGroupsRowGroup,
    periods: ReportPeriodWithLabel[],
): CashFlowStatementViewRow[] {
    const accountCodes = getNestedAccountCodes(group);
    const totals = group.totals ?? [];

    const childRows: CashFlowStatementViewRow[] = buildRowList(
        group.rows,
        periods,
    );

    // Total row for this group
    childRows.push({
        id: nextId(`total:${group.label}`),
        label: `Total ${group.label}`,
        values: totals,
        isTotal: true,
        isSummary: false,
        isExpandable: false,
        accountCodes,
        periods,
    });

    return childRows;
}

function buildRowList(
    rows: ReportWithNestedGroupsRow[],
    periods: ReportPeriodWithLabel[],
): CashFlowStatementViewRow[] {
    const result: CashFlowStatementViewRow[] = [];

    for (const row of rows) {
        if ("rows" in row) {
            // Nested group — recurse
            result.push({
                id: nextId(`group:${row.label}`),
                label: row.label,
                values: row.totals ?? [],
                isTotal: false,
                isSummary: false,
                isExpandable: true,
                accountCodes: getNestedAccountCodes(row),
                periods,
                children: buildGroupChildren(row, periods),
            });
        } else {
            // Leaf line
            result.push({
                id: nextId(`line:${row.label}`),
                label: row.label,
                values: row.values,
                isTotal: false,
                isSummary: false,
                isExpandable: false,
                accountCodes: [row.key],
                periods,
            });
        }
    }

    return result;
}

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

/**
 * Converts a CashFlowStatementResponse into a nested row model for ViewTable.
 * The three sections (Operating / Investing / Financing) become top-level
 * expandable rows whose children are the section's own rows plus a trailing
 * "Net cash from <section>" total row. After the sections, three flat summary
 * rows (Net cash change / Beginning / Ending) are appended as additional
 * top-level rows.
 */
export function buildCashFlowStatementRows(
    report: CashFlowStatementResponse,
): CashFlowStatementViewRow[] {
    rowIdCounter = 0;
    const { periods, sections, summary } = report;

    const rows: CashFlowStatementViewRow[] = sections.map((section) => {
        const sectionAccountCodes = section.rows.flatMap(getNestedAccountCodes);
        const sectionChildren = buildRowList(section.rows, periods);

        // Append the section total as the last child of the section header.
        sectionChildren.push({
            id: nextId(`sectionTotal:${section.section}`),
            label: SECTION_TOTAL_LABELS[section.section],
            values: section.totals,
            isTotal: true,
            isSummary: false,
            isExpandable: false,
            accountCodes: sectionAccountCodes,
            periods,
        });

        return {
            id: nextId(`section:${section.section}`),
            label: SECTION_LABELS[section.section],
            values: section.totals,
            isTotal: false,
            isSummary: false,
            isExpandable: true,
            accountCodes: sectionAccountCodes,
            periods,
            children: sectionChildren,
        };
    });

    rows.push(
        {
            id: nextId("summary:netCashChange"),
            label: "Net cash change",
            values: summary.netCashChange,
            isTotal: false,
            isSummary: true,
            isExpandable: false,
            accountCodes: [],
            periods,
        },
        {
            id: nextId("summary:beginningCashBalance"),
            label: "Beginning cash balance",
            values: summary.beginningCashBalance,
            isTotal: false,
            isSummary: false,
            isExpandable: false,
            accountCodes: [],
            periods,
        },
        {
            id: nextId("summary:endingCashBalance"),
            label: "Ending cash balance",
            values: summary.endingCashBalance,
            isTotal: false,
            isSummary: true,
            isExpandable: false,
            accountCodes: [],
            periods,
        },
    );

    return rows;
}
